import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle,
  CheckIcon,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import NotFound from '../components/NotFound';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from './Register/Navbar';
import PricingCard from './PricingCard/PricingCard';
import ToggleSwitch from './Switch/ToggleSwitch';
import LoadingSpinner from './LoadingSpinner';
import toast, { Toaster } from 'react-hot-toast';
import subsid from '../enums/SubsciptionsType';
// Provider Icons
import cashIcon from '../assets/Payment/banknote.svg';
import wishIcon from '../assets/Payment/whish.svg';
import Support from '../components/Support';
import Footer from './Register/Footer';
import ExpiredLink from '../components/expiredLink';

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getDateAfterOneMonth = () => {
  const now = new Date();
  now.setMonth(now.getMonth() + 1);
  return now.toISOString();
};

const getDateAfterOneMonthWithTrial = (x) => {
  const now = new Date();
  if (typeof x === 'number' && !isNaN(x) && x !== 0) {
    now.setDate(now.getDate() + x);
  }

  now.setMonth(now.getMonth() + 1);
  return now.toISOString();
};

const getDateAfterOneYearWithTrial = (x) => {
  const now = new Date();

  if (typeof x === 'number' && !isNaN(x) && x !== 0) {
    now.setDate(now.getDate() + x);
  }

  now.setFullYear(now.getFullYear() + 1);

  return isNaN(now.getTime()) ? null : now.toISOString(); // Extra safeguard
};

// const getDateAfterOneYearWithTrial = (x) => {
//   const now = new Date();
//   // Add x days to the current date
//     now.setDate(now.getDate() + x);
//     // Then add one year
//     now.setFullYear(now.getFullYear() + 1);
//     return now.toISOString();

// };

const getDateAfterOneYear = () => {
  const now = new Date();
  now.setFullYear(now.getFullYear() + 1);
  return now.toISOString();
};

const deobfuscate = (obfuscated) => {
  const prefix = 'XYZ789';
  const suffix = 'LMN33245';
  const midInsert = '123STPK';

  let stripped = obfuscated;

  // Remove prefix and suffix
  if (stripped.startsWith(prefix)) stripped = stripped.slice(prefix.length);
  if (stripped.endsWith(suffix)) stripped = stripped.slice(0, -suffix.length);

  // Remove midInsert
  const mid = Math.floor(stripped.length / 2);
  const start = stripped.slice(0, mid - Math.floor(midInsert.length / 2));
  const end = stripped.slice(mid + Math.ceil(midInsert.length / 2));

  return start + end;
};

const paymentMethods = [
  {
    id: '0',
    label: 'Cash',
    src: cashIcon,
    alt: 'Cash Payment',
  },
  {
    id: '1',
    label: 'Whish Money',
    src: wishIcon,
    alt: 'Wish Money',
  },
  // {
  //   id: '2',
  //   label: 'OMT Pay',
  //   src: omtIcon,
  //   alt: 'OMT Pay',
  // },
  // {
  //   id: '3',
  //   label: 'MyMonty Payment',
  //   src: montyIcon,
  //   alt: 'MyMonty',
  // },
  // {
  //   id: '4',
  //   label: 'Bob Finance',
  //   src: bobIcon,
  //   alt: 'Bob Finance',
  // },
  //SaVio
];

const PaymentBanner = ({
  isOpen,
  onClose,
  selectedPlan,
  onConfirm,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isAnnual,
  isLoading,
  currentSubs,
  isDataLoading,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Skeleton component for loading states
  const Skeleton = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center'>
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className='fixed inset-0 bg-black/40 backdrop-blur-sm'
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{
            duration: 0.3,
            type: 'spring',
            stiffness: 300,
            damping: 25,
          }}
          className='relative w-full mx-4 bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col'>
          <div className='w-full bg-gradient-to-r from-teal-600 to-teal-500 text-white py-4 px-6 flex justify-between items-center'>
            <motion.h2
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className='text-xl font-hurmeSemiBold'>
              Review & Pay:
            </motion.h2>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className='text-white/90 hover:text-white transition-colors rounded-full p-1 hover:bg-white/10'
              aria-label='Close'>
              <X className='h-6 w-6' />
            </motion.button>
          </div>

          {/* Main Content */}
          <div className='w-full px-4 sm:px-6 lg:px-8 py-6 flex-1'>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
              {/* Plan Details Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}>
                <div className='bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border border-teal-200'>
                  <div className='space-y-4'>
                    {isDataLoading ? (
                      <>
                        <div className='flex justify-between items-center'>
                          <Skeleton className='h-8 w-32' />
                          <Skeleton className='h-6 w-28 rounded-full' />
                        </div>

                        <div className='pt-3 border-t border-teal-200'>
                          <Skeleton className='h-10 w-40 mb-2' />
                          {isAnnual && <Skeleton className='h-5 w-48' />}
                        </div>

                        <ul className='space-y-3 pt-4'>
                          <li className='flex items-center'>
                            <Skeleton className='h-5 w-5 mr-3 rounded-full' />
                            <Skeleton className='h-5 w-32' />
                          </li>
                          <li className='flex items-center'>
                            <Skeleton className='h-5 w-5 mr-3 rounded-full' />
                            <Skeleton className='h-5 w-40' />
                          </li>
                          <li className='flex items-center'>
                            <Skeleton className='h-5 w-5 mr-3 rounded-full' />
                            <Skeleton className='h-5 w-36' />
                          </li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <div className='flex justify-between items-center'>
                          <h3 className='text-2xl font-hurmeSemiBold text-teal-800'>
                            {selectedPlan?.name}
                          </h3>
                          <div className='bg-white shadow-sm border border-teal-200 rounded-full px-3 py-1 text-xs font-medium text-teal-700'>
                            {isAnnual ? 'Annual Billing' : 'Monthly Billing'}
                          </div>
                        </div>

                        <div className='pt-3 border-t border-teal-200'>
                          <div className='text-3xl font-hurmeBold text-gray-900'>
                            $
                            {isAnnual
                              ? selectedPlan?.yearlyCost
                              : selectedPlan?.cost?.toFixed(2) - 0.01}
                            <span className='text-sm font-hurme text-gray-500 ml-1'>
                              / {isAnnual ? 'Year' : 'Month'}
                            </span>
                          </div>
                          {isAnnual && (
                            <div className='text-sm text-teal-700 mt-1'>
                              Save 20% With Annual Billing
                            </div>
                          )}
                        </div>

                        <ul className='space-y-3 pt-4'>
                          <motion.li
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className='flex items-center text-gray-700'>
                            <CheckCircle className='h-5 w-5 text-teal-500 mr-3 flex-shrink-0' />
                            <span>All Basic Features</span>
                          </motion.li>
                          <motion.li
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 }}
                            className='flex items-center text-gray-700'>
                            <CheckCircle className='h-5 w-5 text-teal-500 mr-3 flex-shrink-0' />
                            <span>Priority Customer Support</span>
                          </motion.li>
                          <motion.li
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 }}
                            className='flex items-center text-gray-700'>
                            <CheckCircle className='h-5 w-5 text-teal-500 mr-3 flex-shrink-0' />
                            <span>Unlimited Projects</span>
                          </motion.li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Payment Methods */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}>
                <h3 className='text-lg font-hurmeSemiBold text-gray-900 mb-4'>
                  Select Payment Method:
                </h3>

                <div className='space-y-3'>
                  {isDataLoading ? (
                    // Skeleton for payment methods
                    <>
                      {[1, 2, 3].map((index) => (
                        <div
                          key={index}
                          className='w-full p-4 rounded-lg border-2 border-gray-200'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                              <Skeleton className='w-10 h-10 mr-4 rounded-md' />
                              <div>
                                <Skeleton className='w-28 h-5 mb-1' />
                                <Skeleton className='w-40 h-3' />
                              </div>
                            </div>
                            <Skeleton className='h-6 w-6 rounded-full' />
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      {paymentMethods.map((option, index) => (
                        <motion.button
                          key={option.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          whileHover={{
                            scale: 1.01,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => setSelectedPaymentMethod(option.id)}
                          className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                            selectedPaymentMethod === option.id
                              ? 'border-teal-500 bg-teal-50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}>
                          <div className='flex items-center'>
                            <div className='w-10 h-10 flex items-center justify-center mr-4 bg-white rounded-md p-1 border border-gray-100'>
                              <img
                                src={option.src}
                                alt={option.alt}
                                className='w-full h-full object-contain'
                              />
                            </div>
                            <div>
                              <span className='font-medium text-gray-800'>
                                {option.label}
                              </span>
                              {option.subtitle && (
                                <p className='text-xs text-gray-500 mt-0.5'>
                                  {option.subtitle}
                                </p>
                              )}
                            </div>
                          </div>

                          <div
                            className={`h-6 w-6 rounded-full flex items-center justify-center ${
                              selectedPaymentMethod === option.id
                                ? 'bg-teal-500'
                                : 'border-2 border-gray-300'
                            }`}>
                            {selectedPaymentMethod === option.id && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                  type: 'spring',
                                  stiffness: 400,
                                  damping: 10,
                                }}>
                                <CheckIcon className='h-4 w-4 text-white' />
                              </motion.div>
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </>
                  )}
                </div>
              </motion.div>

              {/* Order Summary */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}>
                <div className='bg-gray-50 rounded-xl p-6 border border-gray-200'>
                  <h3 className='text-lg font-hurmeSemiBold text-gray-900 mb-4'>
                    Order Summary:
                  </h3>

                  <div className='space-y-3'>
                    {isDataLoading ? (
                      <>
                        <div className='flex justify-between'>
                          <span className='text-gray-600'>Subtotal:</span>
                          <Skeleton className='w-20 h-5' />
                        </div>

                        {isAnnual && (
                          <div className='flex justify-between'>
                            <span className='text-gray-600'>
                              Annual discount (20%):
                            </span>
                            <Skeleton className='w-20 h-5' />
                          </div>
                        )}

                        <div className='flex justify-between'>
                          <span className='text-gray-600'>Trial Days:</span>
                          <Skeleton className='w-16 h-5' />
                        </div>

                        <div className='flex justify-between'>
                          <span className='text-gray-600'>
                            Subscription End Date:
                          </span>
                          <Skeleton className='w-32 h-5' />
                        </div>

                        <div className='pt-3 border-t border-gray-200 flex justify-between font-medium text-gray-900'>
                          <span>Total</span>
                          <Skeleton className='w-24 h-6' />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className='flex justify-between text-gray-600'>
                          <span>Subtotal:</span>
                          <span>
                            $
                            {isAnnual
                              ? selectedPlan?.cost?.toFixed(2) * 12 - 0.01
                              : selectedPlan?.cost?.toFixed(2) - 0.01}
                          </span>
                        </div>

                        {isAnnual && (
                          <div className='flex justify-between text-gray-600'>
                            <span>Annual discount (20%):</span>
                            <span className='text-teal-600'>
                              -$
                              {(selectedPlan?.cost * 12 * 0.2 - 0.01).toFixed(
                                2
                              )}
                            </span>
                          </div>
                        )}

                        {selectedPlan?.isTrialPeriodAvailable && (
                          <div className='flex justify-between text-gray-600'>
                            <span>Trial Days:</span>
                            <span>{selectedPlan?.trialPeriod} Days</span>
                          </div>
                        )}

                        <div className='flex justify-between text-gray-600'>
                          <span>Subscription End Date:</span>
                          <span>
                            {formatDate(
                              isAnnual
                                ? getDateAfterOneYearWithTrial(
                                    selectedPlan?.trialPeriod
                                  )
                                : getDateAfterOneMonthWithTrial(
                                    selectedPlan?.trialPeriod
                                  )
                            )}
                          </span>
                        </div>

                        {/* Warning Type Message */}
                        {selectedPlan?.name === 'Regular' &&
                          currentSubs?.packageName === 'Basic' && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className='flex items-start p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                              <AlertTriangle className='h-5 w-5 text-amber-600 flex-shrink-0 mr-2 mt-0.5' />
                              <p className='text-sm text-amber-700'>
                                Please note: Upgrading from Basic plan will
                                result in losing your remaining subscription
                                days.
                              </p>
                            </motion.div>
                          )}

                        <motion.div
                          className='pt-3 border-t border-gray-200 flex justify-between font-medium text-gray-900'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}>
                          <span>Total</span>
                          <span>
                            $
                            {isAnnual
                              ? selectedPlan?.yearlyCost
                              : selectedPlan?.cost?.toFixed(2) - 0.01}
                          </span>
                        </motion.div>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='w-full border-t border-gray-200 bg-gray-50 px-4 sm:px-6 lg:px-8 py-4'>
            <div className='flex flex-col sm:flex-row justify-end gap-3 max-w-lg ml-auto'>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                disabled={isLoading || isDataLoading}
                className='w-full sm:w-40 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 transition-all order-2 sm:order-1 disabled:opacity-50'>
                Cancel
              </motion.button>

              <motion.button
                whileHover={!isLoading && !isDataLoading ? { scale: 1.02 } : {}}
                whileTap={!isLoading && !isDataLoading ? { scale: 0.98 } : {}}
                onClick={onConfirm}
                disabled={isLoading || isDataLoading}
                className='w-full sm:w-64 py-3 px-4 rounded-lg bg-gradient-to-r from-teal-600 to-teal-500 text-white font-medium hover:from-teal-700 hover:to-teal-600 shadow-sm disabled:opacity-80 transition-all order-1 sm:order-2'>
                {isLoading ? (
                  <div className='flex items-center justify-center'>
                    <Loader2 className='animate-spin mr-2 h-5 w-5' />
                    <span>Processing payment...</span>
                  </div>
                ) : isDataLoading ? (
                  <div className='flex items-center justify-center'>
                    <Loader2 className='animate-spin mr-2 h-5 w-5' />
                    <span>Loading details...</span>
                  </div>
                ) : (
                  'Confirm Payment'
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const PaymentBannerMobile = ({
  isOpen,
  onClose,
  selectedPlan,
  onConfirm,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isAnnual,
  isLoading,
  currentSubs,
  isDataLoading,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Skeleton component for loading states
  const Skeleton = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm overflow-y-hidden'
          onClick={onClose}>
          <motion.div
            initial={{ y: '100%', scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: '100%', scale: 0.95 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 0.5,
            }}
            key={isOpen}
            className='relative w-full sm:max-w-lg mx-auto overflow-hidden bg-white sm:rounded-lg shadow-xl max-h-[85vh] sm:max-h-[85vh] flex flex-col rounded-t-3xl'
            onClick={(e) => e.stopPropagation()}
            drag='y'
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, { offset, velocity }) => {
              if (offset.y > 100 || velocity.y > 500) {
                onClose();
              }
            }}>
            {/* Drag handle */}
            <motion.div
              className='flex justify-center pt-3 pb-1 touch-none'
              whileTap={{ scale: 0.95 }}
              onClick={onClose}>
              <div className='w-12 h-1 bg-gray-300 rounded-full' />
            </motion.div>

            {/* Header */}
            <div className='relative px-4 sm:px-6 py-2 text-center'>
              <div className='flex items-center justify-center'>
                <h2 className='text-lg font-hurmeSemiBold text-black underline underline-offset-4 decoration-2'>
                  Review & Pay:
                </h2>
              </div>
            </div>

            <div className='p-4 sm:p-6 overflow-y-auto flex-1'>
              <div className='space-y-6'>
                {/* Order Summary Section */}
                <div className='space-y-2'>
                  <h3 className='text-sm font-hurmeSemiBold text-gray-700'>
                    Order Summary:
                  </h3>
                  <div className='bg-gray-50 rounded-lg border border-gray-200 p-4'>
                    <div className='space-y-3'>
                      {isDataLoading ? (
                        <>
                          {/* Skeleton for subtotal */}
                          <div className='flex justify-between text-sm'>
                            <span className='text-gray-600'>Subtotal:</span>
                            <Skeleton className='w-16 h-5' />
                          </div>

                          {/* Skeleton for annual discount if applicable */}
                          {isAnnual && (
                            <div className='flex justify-between text-sm'>
                              <span className='text-gray-600'>
                                Annual discount (20%)
                              </span>
                              <Skeleton className='w-16 h-5' />
                            </div>
                          )}

                          {/* Skeleton for trial days */}
                          <div className='flex justify-between text-sm'>
                            <span className='text-gray-600'>Trial Days:</span>
                            <Skeleton className='w-16 h-5' />
                          </div>

                          {/* Skeleton for subscription end date */}
                          <div className='flex justify-between text-sm'>
                            <span className='text-gray-600'>
                              Subscription End Date:
                            </span>
                            <Skeleton className='w-24 h-5' />
                          </div>

                          {/* Skeleton for total */}
                          <div className='pt-3 border-t border-gray-200 flex justify-between items-center'>
                            <span className='font-medium text-gray-900'>
                              Total
                            </span>
                            <div className='flex items-center'>
                              <Skeleton className='w-20 h-6 mr-1' />
                              <span className='text-sm text-gray-500'>
                                / {isAnnual ? 'Year' : 'Month'}
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='flex justify-between text-sm text-gray-600'>
                            <span>Subtotal:</span>
                            <span>
                              $
                              {isAnnual
                                ? selectedPlan?.cost?.toFixed(2) * 12 - 0.01
                                : selectedPlan?.cost?.toFixed(2) - 0.01}
                            </span>
                          </div>
                          {isAnnual && (
                            <div className='flex justify-between text-sm text-gray-600'>
                              <span>Annual discount (20%)</span>
                              <span className='text-teal-600'>
                                -$
                                {(selectedPlan?.cost * 12 * 0.2 - 0.01).toFixed(
                                  2
                                )}
                              </span>
                            </div>
                          )}
                          {selectedPlan?.isTrialPeriodAvailable && (
                            <div className='flex justify-between text-sm text-gray-600'>
                              <span>Trial Days:</span>
                              <span>{selectedPlan?.trialPeriod} Days</span>
                            </div>
                          )}

                          <div className='flex justify-between text-sm text-gray-600'>
                            <span>Subscription End Date:</span>
                            <span>
                              {formatDate(
                                isAnnual
                                  ? getDateAfterOneYearWithTrial(
                                      selectedPlan?.trialPeriod
                                    )
                                  : getDateAfterOneMonthWithTrial(
                                      selectedPlan?.trialPeriod
                                    )
                              )}
                            </span>
                          </div>

                          {selectedPlan?.name === 'Regular' &&
                            currentSubs?.packageName === 'Basic' && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className='mt-2 flex items-start p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                                <AlertTriangle className='h-4 w-4 text-amber-600 flex-shrink-0 mr-2 mt-0.5' />
                                <p className='text-xs text-amber-700'>
                                  Please note: Upgrading from Basic plan will
                                  result in losing your remaining subscription
                                  days.
                                </p>
                              </motion.div>
                            )}
                          <div className='pt-3 border-t border-gray-200 flex justify-between items-center'>
                            <span className='font-medium text-gray-900'>
                              Total
                            </span>
                            <div>
                              <span className='font-hurmeBold text-gray-900'>
                                $
                                {isAnnual
                                  ? selectedPlan?.yearlyCost
                                  : selectedPlan?.cost?.toFixed(2) - 0.01}
                              </span>
                              <span className='text-sm text-gray-500 ml-1'>
                                / {isAnnual ? 'Year' : 'Month'}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className='space-y-3'>
                  <h3 className='text-sm font-hurmeSemiBold text-gray-700'>
                    Payment Method:
                  </h3>

                  <div className='space-y-2'>
                    {isDataLoading ? (
                      // Skeleton for payment methods
                      <>
                        <div className='w-full h-16 rounded-md border border-gray-200 p-4'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                              <Skeleton className='w-8 h-8 mr-3' />
                              <Skeleton className='w-24 h-5' />
                            </div>
                            <Skeleton className='w-5 h-5 rounded-full' />
                          </div>
                        </div>
                        <div className='w-full h-16 rounded-md border border-gray-200 p-4'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                              <Skeleton className='w-8 h-8 mr-3' />
                              <Skeleton className='w-24 h-5' />
                            </div>
                            <Skeleton className='w-5 h-5 rounded-full' />
                          </div>
                        </div>
                      </>
                    ) : (
                      paymentMethods.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setSelectedPaymentMethod(option.id)}
                          className={`w-full flex items-center justify-between p-4 rounded-md border transition-all ${
                            selectedPaymentMethod === option.id
                              ? 'border-teal-500 bg-teal-50/50 ring-1 ring-teal-500'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}>
                          <div className='flex items-center'>
                            <div className='w-8 h-8 flex items-center justify-center mr-3'>
                              <img
                                src={option.src}
                                alt={option.alt}
                                className='w-full h-full object-contain'
                              />
                            </div>
                            <span className='font-medium text-sm text-gray-800'>
                              {option.label}
                            </span>
                          </div>

                          <div
                            className={`h-5 w-5 rounded-full flex items-center justify-center ${
                              selectedPaymentMethod === option.id
                                ? 'bg-teal-500'
                                : 'border border-gray-300'
                            }`}>
                            {selectedPaymentMethod === option.id && (
                              <CheckIcon className='h-3 w-3 text-white' />
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='p-4 sm:p-6 border-t border-gray-200 bg-gray-50'>
              <div className='space-y-3'>
                <button
                  onClick={onConfirm}
                  disabled={isLoading || isDataLoading}
                  className='w-full py-3 px-4 rounded-md bg-gradient-to-r from-teal-600 to-teal-700 text-white font-medium hover:from-teal-700 hover:to-teal-800 disabled:opacity-50 transition-colors text-sm flex items-center justify-center'>
                  {isLoading ? (
                    <>
                      <Loader2 className='animate-spin mr-2 h-4 w-4' />
                      <span>Processing payment...</span>
                    </>
                  ) : isDataLoading ? (
                    <>
                      <Loader2 className='animate-spin mr-2 h-4 w-4' />
                      <span>Loading details...</span>
                    </>
                  ) : (
                    'Confirm Payment'
                  )}
                </button>

                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className='w-full py-3 px-4 rounded-md bg-transparent text-gray-600 font-medium hover:bg-gray-100 transition-colors text-sm'>
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
// Hero Section Component

export default function Subscriptions() {
  const { businessid, userid, token, selectedplan, isyearly, WLR } =
    useParams();
  const businesId = businessid.slice(0, -22);
  const userId = userid.slice(0, -22);
  const Token = token.slice(0, -22);
  const SelectedPlan = selectedplan.slice(0, -22);
  const IsYearly = isyearly.slice(0, -22);
  const Start = WLR;
  const [isAnnual, setIsAnnual] = useState(IsYearly === 1 ? true : false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [subs, setSubs] = useState([]);
  const [currentSubs, setCurrentSubs] = useState();
  const [isViewVisible, setIViewVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [screenHeight, setScreenHeight] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [isValidChecked, setIsValidChecked] = useState(false);
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };

  const childVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 120 },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
  };

  const handleUpgradeClick = (item) => {
    setSelectedSubscription(item);
    setIViewVisible(true);
  };

  useEffect(() => {
    const Item = subs.find((item) => item.id === SelectedPlan);

    setTimeout(() => {
      setSelectedSubscription(Item);
      setIViewVisible(true);
    }, 100);
  }, [SelectedPlan, subs]);

  const handleConfirmUpgrade = () => {
    if (selectedPaymentMethod === '0') {
      CashUpgrade(selectedSubscription);
    } else {
      WhishPaymentUpgrade(selectedSubscription);
    }
  };

  const CashUpgrade = async (x) => {
    try {
      setIsLoading(true);
      const req = await fetch(
        'https://api.thebooker.online/api/SubscriptionRequested',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${Token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            businessId: businesId,
            userId: userId,
            isTrialPeriodAvailable: false,
            trialPeriodByDays: 0,
            endDate: isAnnual ? getDateAfterOneYear() : getDateAfterOneMonth(),
            startDate: new Date(),
            requestedSubscriptionTypeId: x.id,
            requestedSubscriptionTypeName: x.name,
            description: `TheBooker Business - Upgrade to ${x.name}`,
            oldSubscriptionPlanId: currentSubs.subscriptionTypeId,
            oldSubscriptionPlanName: currentSubs.packageName,
            subscriptionId: currentSubs.subscriptionId,
          }),
        }
      );

      if (req.status === 200 || req.status === 201) {
        getSubscriptionType();
        getBusinessSubscription();
        navigate('/pending-subscription');
      } else if (req.status === 400 || req.status === 401) {
        navigate('/pending-subscription');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const WhishPaymentUpgrade = async (x) => {
    try {
      setIsLoading(true);

      const req = await fetch(
        'https://api.thebooker.online/api/Subscription/thirdPartyLinkOnPayment',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${Token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            businessId: businesId,
            isAutoRenewal: false,
            invoiceDescription: `TheBooker Business - Upgrade to ${x.name}`,
            currency: 0,
            userId: userId,
            isTrialPeriodAvailable: false,
            trialPeriodInMonths: 0,
            endDate: isAnnual ? getDateAfterOneYear() : getDateAfterOneMonth(),
            requestedSubscritionTypeId: x.id,
            previousSubscritionTypeId: currentSubs.subscriptionTypeId,
            subscriptionId: currentSubs.subscriptionId,
            period: isAnnual ? 2 : 1,
            paymentMethod: Number(selectedPaymentMethod),
            startDate: new Date(),
            amount: x.cost,
          }),
        }
      );

      if (req.status === 200) {
        const json = await req.json();
        const paymentLink = json.link;

        // Detect iOS Safari
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );

        if (isIOS) {
          // Special handling for iOS devices
          // For iOS Safari, direct navigation works best
          window.location.href = paymentLink;

          // Show a toast before navigation
          toast.success('Redirecting to payment page...', {
            duration: 3000,
          });
        } else if (isSafari) {
          // Desktop Safari approach
          const tempLink = document.createElement('a');
          tempLink.href = paymentLink;
          tempLink.target = '_blank';
          tempLink.rel = 'noopener noreferrer';
          tempLink.style.display = 'none';
          document.body.appendChild(tempLink);

          toast.success('Payment page opening...', {
            duration: 3000,
          });

          tempLink.click();
          window.location.href = paymentLink;
          setTimeout(() => {
            document.body.removeChild(tempLink);
          }, 100);
        } else {
          // For other browsers
          try {
            const newWindow = window.open(paymentLink, '_blank');

            if (
              !newWindow ||
              newWindow.closed ||
              typeof newWindow.closed === 'undefined'
            ) {
              window.location.href = paymentLink;
            }
          } catch (e) {
            console.error('Error opening payment window:', e);
            window.location.href = paymentLink;
          }
        }

        setIViewVisible(false);
      } else if (req.status === 400) {
        const json = await req.json();
        toast.error(json.errors ? String(json.errors[0]) : json.message);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const checkValidURL = useCallback(async () => {
    // try {
    //   const req = await fetch(
    //     `https://api.thebooker.online/api/Auth/checkToken/${Token}`,
    //     {
    //       method: 'GET',
    //       headers: {
    //         Authorization: `Bearer ${Token}`,
    //         'Cache-Control': 'no-cache',
    //         Accept: 'application/json',
    //         'Content-Type': 'application/json',
    //       },
    //     }
    //   );

    //   if (req.status === 200) {
    //     const json = await req.json();
    //     setIsValid(Boolean(json.valid));
    //   } else if (req.status === 400) {
    //     const json = await req.json();
    //     toast.error(json.errors ? String(json.errors[0]) : json.message);
    //     setIsValid(false);
    //   } else if (req.status === 401) {
    //     setInvalidToken(true);
    //     setIsValid(false);
    //   }
    // } catch (err) {
    //   console.error(err);
    //   toast.error(err.message || 'An error occurred');
    //   setIsValid(false);
    // } finally {
    //   setIsValidChecked(true);
    //   setIsDataLoading(false);
    // }
    try {
      const x = deobfuscate(Start);
      const inputDate = new Date(x);
      const now = new Date();
      const timeDifference = now - inputDate;
      const hoursDifference = timeDifference / (1000 * 60 * 60);
      const result = hoursDifference <= 2;
      if (result) setIsValid(true);
      else setIsValid(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsValidChecked(true);
      setIsDataLoading(false);
    }
  }, [Start]);

  const getSubscriptionType = useCallback(async () => {
    try {
      const req = await fetch(
        `https://api.thebooker.online/api/SubscriptionType/dropdown`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${Token}`,
            'Cache-Control': 'no-cache',
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (req.status === 200) {
        const json = await req.json();
        setSubs(json);
      } else if (req.status === 400) {
        const json = await req.json();
        toast.error(json.errors ? String(json.errors[0]) : json.message);
      } else if (req.status === 401) {
        setInvalidToken(true);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'An error occurred');
    } finally {
      setIsDataLoading(false);
    }
  }, [Token]);

  const getBusinessSubscription = useCallback(async () => {
    try {
      let url = `https://api.thebooker.online/api/Subscription/checkBusinessSubscription/${businesId}`;
      const req = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${Token}`,
          'Cache-Control': 'no-cache',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (req.status === 200) {
        const json = await req.json();
        setCurrentSubs(json);
        setInvalidToken(false);
      } else if (req.status === 400) {
        const json = await req.json();
        toast.error(json.errors ? String(json.errors[0]) : json.message);
      } else if (req.status === 401) {
        setInvalidToken(true);
      }
    } catch (error) {
      console.error({ error });
      toast.error(error.message || 'An error occurred');
    } finally {
      setPageLoading(false);
    }
  }, [businesId, Token]);

  useEffect(() => {
    setPageLoading(true);
    getSubscriptionType();
    getBusinessSubscription();
    checkValidURL();
    window.scrollTo(0, 0);
    window.document.title = 'TheBooker - Subscription';
  }, [getSubscriptionType, getBusinessSubscription, checkValidURL]);

  useEffect(() => {
    const updateHeight = () => {
      setScreenHeight(window.innerHeight);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsMobile(/android|iphone|ipad|ipod/i.test(userAgent));

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [screenHeight]);

  if (!isValidChecked) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 font-hurme'>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}>
          <LoadingSpinner size={60} />
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className='mt-4 text-black font-HurmeBold'>
          Loading subscription plans...
        </motion.p>
      </div>
    );
  }

  if (invalidToken) {
    return <NotFound />;
  }

  if (!isValid) {
    return <ExpiredLink isMobile={isMobile} />;
  }

  if (pageLoading) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 font-hurme'>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}>
          <LoadingSpinner size={60} />
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className='mt-4 text-black font-HurmeBold'>
          Loading subscription plans...
        </motion.p>
      </div>
    );
  }

  return (
    <>
      <Toaster />
      <Support />
      <motion.div
        initial='hidden'
        animate='visible'
        variants={containerVariants}
        className='min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-hurme'>
        <Navbar isRegisterPage={false} Translated={false} />
        <div className='mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-16 lg:px-8'>
          <motion.div variants={childVariants} className='text-center'>
            <motion.h1
              variants={childVariants}
              className='bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-4xl font-hurmeBold tracking-tight text-transparent sm:text-5xl lg:text-6xl'>
              Choose A Plan:
            </motion.h1>
            <motion.p
              variants={childVariants}
              className='mt-2 sm:mt-3 text-base sm:text-lg text-gray-600 font-hurmeSemiBold'>
              Current plan:{' '}
              <AnimatePresence>
                {isLoading ? (
                  <motion.div
                    key='loading'
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ yoyo: Infinity, duration: 0.8 }}
                    className='w-24 h-6 bg-gray-200 rounded-md inline-block align-middle'></motion.div>
                ) : (
                  <motion.span
                    key='plan-name'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className='font-semibold text-booker'>
                    {currentSubs?.packageName}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.p>
          </motion.div>

          <motion.div
            variants={childVariants}
            className='mt-4 sm:mt-8 flex justify-center'>
            <ToggleSwitch isAnnual={isAnnual} setIsAnnual={setIsAnnual} />
          </motion.div>

          <motion.div
            variants={fadeInVariants}
            className='mt-5 sm:mt-12 grid gap-4 sm:gap-8 md:grid-cols-2 lg:grid-cols-3'>
            <AnimatePresence>
              {subs
                .filter((sub) => {
                  const x = [
                    subsid.FreemiumID,
                    subsid.BasicID,
                    subsid.RegularID,
                  ];
                  return x.includes(sub.id);
                })
                .sort((a, b) => {
                  const order = [
                    subsid.FreemiumID,
                    subsid.BasicID,
                    subsid.RegularID,
                  ];
                  return order.indexOf(a.id) - order.indexOf(b.id);
                })
                .map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    variants={childVariants}
                    custom={index}
                    initial='hidden'
                    animate='visible'
                    exit={{ opacity: 0, y: 20, transition: { duration: 0.2 } }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                    <PricingCard
                      plan={plan}
                      isAnnual={isAnnual}
                      currentSubs={currentSubs}
                      onUpgrade={handleUpgradeClick}
                      isPopular={plan.name === 'Basic'}
                    />
                  </motion.div>
                ))}
            </AnimatePresence>
          </motion.div>
        </div>
        {isMobile ? (
          <PaymentBannerMobile
            isOpen={isViewVisible}
            onClose={() => setIViewVisible(false)}
            selectedPlan={selectedSubscription}
            onConfirm={handleConfirmUpgrade}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            isAnnual={isAnnual}
            isLoading={isLoading}
            isDataLoading={isDataLoading}
            currentSubs={currentSubs}
          />
        ) : (
          <PaymentBanner
            isOpen={isViewVisible}
            onClose={() => setIViewVisible(false)}
            selectedPlan={selectedSubscription}
            onConfirm={handleConfirmUpgrade}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            isAnnual={isAnnual}
            isLoading={isLoading}
            isDataLoading={isDataLoading}
            currentSubs={currentSubs}
            className='px-4 sm:px-8'
          />
        )}
        <Footer />
      </motion.div>
    </>
  );
}
